import { Divider, Stack, Typography } from '@chronosphereio/chrono-ui';
import CheckboxBlankIcon from 'mdi-material-ui/CheckboxBlank';
import type { UnitOptions } from '@/core';
import {
  formatDependencyMapValue,
  ResolvedDependencyMapThresholds,
  useDependencyMapConfig,
} from './dependency-map-config';
import {
  AGGREGATE_LINK_STROKE_PX,
  AGGREGATE_STRIPE_OFFSET_PX,
  LINK_WIDTH_MAX_PX,
  LINK_WIDTH_MIN_PX,
} from './link-style';

const SAMPLE_LINE_LENGTH_PX = 18;
const THRESHOLD_SWATCH_SIZE_PX = 12;

interface SampleLineProps {
  /** Mirrors what the cluster actually renders. */
  strokeWidthPx: number;
  /** Same height across all samples so the legend stays on one baseline. */
  heightPx: number;
  color: string;
}

const SampleLine = ({ strokeWidthPx, heightPx, color }: SampleLineProps) => (
  <svg width={SAMPLE_LINE_LENGTH_PX} height={heightPx} aria-hidden focusable="false">
    <line
      x1={0}
      x2={SAMPLE_LINE_LENGTH_PX}
      y1={heightPx / 2}
      y2={heightPx / 2}
      stroke={color}
      strokeWidth={strokeWidthPx}
      strokeLinecap="round"
    />
  </svg>
);

interface AggregateSampleLineProps {
  heightPx: number;
  color: string;
}

/**
 * Two-stripe sample mirroring the renderer's aggregate edge. Both stripes
 * paint the same color — the swatch only communicates the visual idiom, not
 * any specific bucket's threshold makeup.
 */
const AggregateSampleLine = ({ heightPx, color }: AggregateSampleLineProps) => {
  const centerY = heightPx / 2;
  return (
    <svg width={SAMPLE_LINE_LENGTH_PX} height={heightPx} aria-hidden focusable="false">
      <line
        x1={0}
        x2={SAMPLE_LINE_LENGTH_PX}
        y1={centerY - AGGREGATE_STRIPE_OFFSET_PX}
        y2={centerY - AGGREGATE_STRIPE_OFFSET_PX}
        stroke={color}
        strokeWidth={AGGREGATE_LINK_STROKE_PX}
        strokeLinecap="round"
      />
      <line
        x1={0}
        x2={SAMPLE_LINE_LENGTH_PX}
        y1={centerY + AGGREGATE_STRIPE_OFFSET_PX}
        y2={centerY + AGGREGATE_STRIPE_OFFSET_PX}
        stroke={color}
        strokeWidth={AGGREGATE_LINK_STROKE_PX}
        strokeLinecap="round"
      />
    </svg>
  );
};

interface ThresholdEntry {
  color: string;
  label: string;
}

/**
 * Ordered swatches: base first (when named), then each step. Step labels are
 * suffixed with `(≥ value)`; nameless steps fall back to a bare `≥ value`.
 * Uses `≥` to match `matchThresholdStep`. Returns `[]` when nothing meaningful
 * exists; the consumer hides the section in that case.
 */
const buildThresholdEntries = (thresholds: ResolvedDependencyMapThresholds, unit: UnitOptions): ThresholdEntry[] => {
  const entries: ThresholdEntry[] = [];
  if (thresholds.base.name) {
    entries.push({ color: thresholds.base.color, label: thresholds.base.name });
  }

  thresholds.steps.forEach((step) => {
    const cutoff = `≥ ${formatDependencyMapValue(step.value, unit)}`;
    entries.push({
      color: step.color,
      label: step.name ? `${step.name} (${cutoff})` : cutoff,
    });
  });
  return entries;
};

interface LegendItemProps {
  strokeWidthPx: number;
  color: string;
  label: string;
}

// Shared height across all samples (width-encoded and aggregate) so the
// legend stays on one baseline.
const SAMPLE_HEIGHT_PX = LINK_WIDTH_MAX_PX + 2 * AGGREGATE_STRIPE_OFFSET_PX;

const LegendItem = ({ strokeWidthPx, color, label }: LegendItemProps) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <SampleLine strokeWidthPx={strokeWidthPx} heightPx={SAMPLE_HEIGHT_PX} color={color} />
    <Typography variant="caption" sx={{ color: (t) => t.palette.text.legacySecondary, whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
  </Stack>
);

interface AggregateLegendItemProps {
  color: string;
}

/**
 * Mirrors `LegendItem`'s layout so Lower / Higher / Aggregated read as one
 * related group.
 */
const AggregateLegendItem = ({ color }: AggregateLegendItemProps) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <AggregateSampleLine heightPx={SAMPLE_HEIGHT_PX} color={color} />
    <Typography variant="caption" sx={{ color: (t) => t.palette.text.legacySecondary, whiteSpace: 'nowrap' }}>
      Aggregated
    </Typography>
  </Stack>
);

interface ThresholdLegendItemProps {
  color: string;
  label: string;
}

/**
 * Square + label, matching dashboard panel legends so charts and this map
 * read consistently. Color goes through inline `style` (not `sx`) to mirror
 * the dashboard implementation; MDI icons read `color` off the SVG element.
 */
const ThresholdLegendItem = ({ color, label }: ThresholdLegendItemProps) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <CheckboxBlankIcon sx={{ fontSize: `${THRESHOLD_SWATCH_SIZE_PX}px` }} style={{ color }} />
    <Typography variant="caption" sx={{ color: (t) => t.palette.text.legacySecondary, whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
  </Stack>
);

interface DependencyMapLegendProps {
  /** Hide the "Aggregated" sample when no aggregate edges are on screen. */
  hasAggregateLinks: boolean;
}

/**
 * Always shows Lower/Higher width samples drawn at the cluster's actual
 * stroke widths. Conditionally appends "Aggregated" when aggregate edges are
 * on screen (label is intentionally bare — aggregate edges have no single
 * value). Threshold swatches render after a divider when any named buckets
 * exist.
 */
export const DependencyMapLegend = ({ hasAggregateLinks }: DependencyMapLegendProps) => {
  const { thresholds, metricLabel, unit } = useDependencyMapConfig();
  const widthSampleColor = thresholds.base.color;
  const lowLabel = metricLabel ? `Lower ${metricLabel}` : 'Lower value';
  const highLabel = metricLabel ? `Higher ${metricLabel}` : 'Higher value';
  const thresholdEntries = buildThresholdEntries(thresholds, unit);

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <LegendItem strokeWidthPx={LINK_WIDTH_MIN_PX} color={widthSampleColor} label={lowLabel} />
      <LegendItem strokeWidthPx={LINK_WIDTH_MAX_PX} color={widthSampleColor} label={highLabel} />
      {hasAggregateLinks && <AggregateLegendItem color={widthSampleColor} />}
      {thresholdEntries.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          {thresholdEntries.map((entry) => (
            <ThresholdLegendItem key={entry.label} color={entry.color} label={entry.label} />
          ))}
        </>
      )}
    </Stack>
  );
};
